import React from 'react';
import { Formik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import Modal from 'shared/modals/Modal';
import { FormikInput, FormikSelect } from 'shared/forms';
import LoadingModal from 'shared/loading/LoadingModal';
import errorToastr from 'shared/helpers/errorToastr';
import { postOrganization } from 'src/fetch/organizations';
import useStoreLoading from './useStoreLoading';
import { requestStatuses } from 'shared/../constants/store';
import { addOrganization } from 'src/slices/organizations';
import organizationSchema from './organizationSchema';
// selectors
import { getStates } from 'src/slices/states';

const OrganizationFormModal = ({
  isOpen,
  onSaved,
  onClose,
  organizationType,
  initialValues,
}) => {
  const dispatch = useDispatch();

  const { status: loadingStatus } = useStoreLoading();
  const isLoading = [requestStatuses.INIT, requestStatuses.PENDING].includes(loadingStatus);

  const states = useSelector(getStates);

  const onSubmit = async (values, { setSubmitting }) => {
    await postOrganization({ ...values, accountingStatusText: `${values.name} - Pending Approval` })
      .then(({ organization }) => {
        dispatch(addOrganization({ organization }));
        onSaved(organization);
        onClose();
      })
      .catch((error) => {
        errorToastr({
          error,
          messagePrefix: 'Organization Save Failed!'
        });
      })
      .finally(() => {
        setSubmitting(false);
      });
  };
  return (
    <Formik
      enableReinitialize
      validationSchema={organizationSchema}
      initialValues={{
        name: '',
        addresses: [
          {
            address1: '',
            city: '',
            state: 1,
            postalCode: ''
          }
        ],
        roles: [],
        accountingStatus: 0,
        ...(initialValues ?? {})
      }}
      onSubmit={onSubmit}
    >
      {({
        setFieldValue,
        setFieldTouched,
        values,
        handleChange,
        handleBlur,
        handleSubmit,
        touched,
        errors,
        isSubmitting,
        disabled,
      }) => (
        <Modal isOpen={isOpen} handleClose={onClose} containerStyle='medium'>
          <div className='modal-header'>
            <h5 className='modal-title' id='addAdditionalChargeModal'>
              Create a {organizationType}
            </h5>
            <button className='button large close' onClick={onClose} />
          </div>
          <section className={`additional-charges-form modal-body ${(isLoading || isSubmitting) ? 'hide-overflow' : ''}`}>
            <LoadingModal isOpen={isLoading || isSubmitting} isNested={true} />
            {loadingStatus === requestStatuses.FAILED && <div className="feedback">Failed to load Organization form. Try again</div>}
            {loadingStatus === requestStatuses.SUCCESS && (
              <form onSubmit={handleSubmit}>
              <section className='formik-form'>
                <section className='formik-column'>
                  <div className='formik-wrapper'>
                    <FormikInput
                      type='text'
                      label='Name*'
                      name='name'
                      value={values.name}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      touched={touched}
                      errors={errors}
                      disabled={disabled}
                    />
                  </div>
                  <div className='formik-wrapper'>
                    <section className='formik-row'>
                      <FormikInput
                        wrapperClass='wrapper-one-column'
                        type='text'
                        label='Address'
                        name='addresses[0].address1'
                        value={values.addresses.length > 0 ? values.addresses[0].address1 : ''}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        touched={touched}
                        errors={errors}
                        disabled={disabled}
                      />
                    </section>
                  </div>
                  <div className='formik-wrapper'>
                    <section className='formik-row'>
                      <FormikInput
                        wrapperClass='wrapper-three-column'
                        type='text'
                        label='City'
                        name='addresses[0].city'
                        value={values.addresses.length > 0 ? values.addresses[0].city : ''}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        touched={touched}
                        errors={errors}
                        disabled={disabled}
                      />
                      <FormikSelect
                        onChange={selected => setFieldValue('addresses[0].state', selected.value)}
                        wrapperClass='wrapper-three-column'
                        label='State'
                        options={states.map(x => ({ label: x.text, value: x.value }))}
                        name='addresses[0].state'
                        value={values.addresses.length > 0 ? values.addresses[0].state : ''}
                        onBlur={setFieldTouched}
                        touched={touched}
                        errors={errors}
                        disabled={disabled}
                      />
                      <FormikInput
                        wrapperClass='wrapper-three-column'
                        type='text'
                        label='ZIP Code'
                        name='addresses[0].postalCode'
                        value={values.addresses.length > 0 ? values.addresses[0].postalCode : ''}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        touched={touched}
                        errors={errors}
                        disabled={disabled}
                      />
                    </section>
                  </div>
                </section>
              </section>
              <section className='toolbar toolbar--xxs'>
                <div className='toolbar--column toolbar--right'>
                  <button type='submit' className='button large secondary text-align--center toolbar--button--margin' disabled={isLoading || isSubmitting}>
                    Submit
                  </button>
                  <button
                    id='add-additional-charge-cancel'
                    className='button large white text-align--center margin--none--xxs'
                    onClick={onClose}
                  >
                    Cancel
                  </button>
                </div>
              </section>
              </form>
            )}
          </section>
        </Modal>
      )}
    </Formik>
  );
};

OrganizationFormModal.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  onSaved: PropTypes.func,
  initialValues: PropTypes.object,
  organizationType: PropTypes.string,
};

export default OrganizationFormModal;
